import { sanitizeNullStrings } from "@/utils/sanitize";
import { useEffect, useMemo, useState } from "react";
import {
	Box,
	Button,
	Checkbox,
	CircularProgress,
	Divider,
	FormControlLabel,
	Grid2,
	IconButton,
	TextField,
	Tooltip,
	Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import type { CostCenter } from "../models/CostCenter";

interface FormErrors {
	Name?: string;
}

interface CostCenterDetailViewerProps {
	costCenter: CostCenter | null;
	existingCostCenters: CostCenter[];
	isSaving: boolean;
	onSave: (costCenter: CostCenter) => void;
	onClose: () => void;
}

export const CostCenterDetailViewer = ({
	costCenter,
	existingCostCenters,
	isSaving,
	onSave,
	onClose,
}: CostCenterDetailViewerProps) => {
	const [formData, setFormData] = useState<CostCenter | null>(null);
	const [errors, setErrors] = useState<FormErrors>({});
	const [touched, setTouched] = useState<Record<string, boolean>>({});

	const isNewCostCenter = !costCenter?.Id;

	const validateForm = useMemo(() => {
		return (data: CostCenter | null): FormErrors => {
			const newErrors: FormErrors = {};

			if (!data?.Name || data.Name.trim().length === 0) {
				newErrors.Name = "Name is required";
			} else {
				const isDuplicate = existingCostCenters.some(
					(c) =>
						c.Id !== data.Id &&
						c.Name.toLowerCase() === data.Name.trim().toLowerCase(),
				);
				if (isDuplicate) {
					newErrors.Name = "This name already exists";
				}
			}

			return newErrors;
		};
	}, [existingCostCenters]);

	const formErrors = useMemo(
		() => validateForm(formData),
		[formData, validateForm],
	);
	const isFormValid = Object.keys(formErrors).length === 0;
	const canCreate = isNewCostCenter && isFormValid;

	useEffect(() => {
		if (costCenter) {
			setFormData(sanitizeNullStrings({ ...costCenter }));
		} else {
			setFormData(null);
		}
		setErrors({});
		setTouched({});
	}, [costCenter]);

	const handleChange = (field: keyof CostCenter, value: unknown) => {
		setFormData((prev) => (prev ? { ...prev, [field]: value } : null));
	};

	const handleBlur = (field: keyof CostCenter) => {
		setTouched((prev) => ({ ...prev, [field]: true }));
		if (formData) {
			setErrors(validateForm(formData));
		}
	};

	const handleBlurSave = (field: keyof CostCenter, value: unknown) => {
		handleBlur(field);

		if (isNewCostCenter) return;

		const updatedData = formData ? { ...formData, [field]: value } : null;
		const currentErrors = validateForm(updatedData);
		if (currentErrors[field as keyof FormErrors]) return;

		const originalValue = costCenter?.[field];
		const normalizedOriginal = originalValue === null ? "" : originalValue;
		const normalizedNew = value === null ? "" : value;

		if (normalizedOriginal !== normalizedNew) {
			onSave({ ...formData!, [field]: value });
		}
	};

	const handleCheckboxChange = (field: keyof CostCenter, value: unknown) => {
		handleChange(field, value);

		if (isNewCostCenter) return;

		if (costCenter?.[field] !== value) {
			onSave({ ...formData!, [field]: value });
		}
	};

	const handleCreate = () => {
		setTouched({ Name: true });
		setErrors(formErrors);

		if (formData && canCreate) {
			onSave(formData);
		}
	};

	if (!costCenter) {
		return (
			<Box
				sx={{
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
					height: "100%",
					color: "text.secondary",
				}}
			>
				<Typography>Select a cost center to view details</Typography>
			</Box>
		);
	}

	if (!formData) return null;

	return (
		<Box sx={{ p: 1.5, height: "100%", overflow: "auto" }}>
			<Box
				sx={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					mb: 1,
				}}
			>
				<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
					<Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
						{isNewCostCenter ? "New Cost Center" : formData.Name}
					</Typography>
					{isSaving && <CircularProgress size={16} />}
				</Box>
				<Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
					{isNewCostCenter && (
						<Button
							color="primary"
							variant="contained"
							size="small"
							onClick={handleCreate}
							disabled={!canCreate || isSaving}
						>
							{isSaving ? "CREATING..." : "CREATE"}
						</Button>
					)}
					<Tooltip title="Close">
						<IconButton size="small" onClick={onClose} disabled={isSaving}>
							<CloseIcon />
						</IconButton>
					</Tooltip>
				</Box>
			</Box>

			<Divider sx={{ mb: 1.5 }} />

			<Grid2 container spacing={1.5}>
				<Grid2 size={{ xs: 12 }}>
					<Typography
						sx={{
							color: "primary.main",
							fontWeight: 600,
							fontSize: "0.85rem",
							mb: 0.5,
						}}
					>
						Basic Information
					</Typography>
				</Grid2>

				<Grid2 size={{ xs: 12 }}>
					<TextField
						label="Name"
						value={formData.Name}
						onChange={(e) => handleChange("Name", e.target.value)}
						onBlur={(e) => handleBlurSave("Name", e.target.value)}
						fullWidth
						size="small"
						required
						error={touched.Name && !!errors.Name}
						helperText={touched.Name && errors.Name}
						slotProps={{ inputLabel: { shrink: true } }}
					/>
				</Grid2>

				<Grid2 size={{ xs: 12 }}>
					<TextField
						label="Description"
						value={formData.Description ?? ""}
						onChange={(e) => handleChange("Description", e.target.value)}
						onBlur={(e) => handleBlurSave("Description", e.target.value)}
						fullWidth
						size="small"
						slotProps={{ inputLabel: { shrink: true } }}
					/>
				</Grid2>

				<Grid2 size={{ xs: 6 }}>
					<TextField
						label="Bud"
						type="number"
						value={formData.Bud}
						onChange={(e) => handleChange("Bud", Number(e.target.value) || 0)}
						onBlur={(e) => handleBlurSave("Bud", Number(e.target.value) || 0)}
						fullWidth
						size="small"
						slotProps={{ inputLabel: { shrink: true } }}
					/>
				</Grid2>

				<Grid2 size={{ xs: 6 }}>
					<TextField
						label="Dept Id"
						value={formData.DeptId ?? ""}
						onChange={(e) => handleChange("DeptId", e.target.value)}
						onBlur={(e) => handleBlurSave("DeptId", e.target.value)}
						fullWidth
						size="small"
						slotProps={{ inputLabel: { shrink: true } }}
					/>
				</Grid2>

				<Grid2 size={{ xs: 12 }}>
					<TextField
						label="End User"
						value={formData.EndUser ?? ""}
						onChange={(e) => handleChange("EndUser", e.target.value)}
						onBlur={(e) => handleBlurSave("EndUser", e.target.value)}
						fullWidth
						size="small"
						slotProps={{ inputLabel: { shrink: true } }}
					/>
				</Grid2>

				<Grid2 size={{ xs: 12 }}>
					<TextField
						label="Comments"
						value={formData.Comments ?? ""}
						onChange={(e) => handleChange("Comments", e.target.value)}
						onBlur={(e) => handleBlurSave("Comments", e.target.value)}
						fullWidth
						size="small"
						multiline
						rows={3}
						slotProps={{ inputLabel: { shrink: true } }}
					/>
				</Grid2>

				<Grid2 size={{ xs: 12 }}>
					<FormControlLabel
						control={
							<Checkbox
								checked={formData.Active}
								onChange={(e) =>
									handleCheckboxChange("Active", e.target.checked)
								}
								size="small"
							/>
						}
						label="Active"
						sx={{ ml: 0 }}
					/>
				</Grid2>
			</Grid2>
		</Box>
	);
};
